import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../../../components/ui/Card';
import Dialog from '../../../components/ui/Dialog';
import { LoadingSpinner } from '../../../components/ui/LoadingSpinner';
import NameWithEmailTooltip from '../../../components/ui/NameWithEmailTooltip';
import ErrorState from '../../../components/ui/ErrorState';
import { useAuth } from '../../../hooks/useAuth';
import { useNominationToast } from '../../../hooks/useNominationToast';
import { nominationApprovalService, type NominationApprovalItem } from '../../../services';
import tuneIcon from '../../../assets/tune.svg';
import arrowDropDownIcon from '../../../assets/arrow_drop_down_filter.svg';
import SearchFilterBar from '../../../components/ui/SearchFilterBar';
import FiltersModal from '../../../components/ui/FiltersModal';
import Pagination from '../../../components/ui/Pagination';
import NoActiveCycle from '../../../components/ui/NoActiveCycle';
import { useActiveCycle } from '../../../hooks/useActiveCycle';

// Constants
const PageSizeOptions = [10, 20, 30, 50];

// Nomination Approval interface
interface NominationApproval {
  id: string;
  employeeId: string;
  employeeName: string;
  designation: string;
  department: string;
  managerName: string;
  nominationStatus: 'Approved' | 'Pending' | 'Rejected';
  employeeEmail: string;
  managerEmail?: string;
  okrStatus?: string;
  okrId?: number;
  nominationId?: number | null; // Track if nomination_id exists
}

const NominationApprovalPageView: React.FC = () => {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const hasActiveCycle = useActiveCycle();
  const { toastState, showError, closeToast } = useNominationToast();
  const [nominations, setNominations] = useState<NominationApproval[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Track if data has been fetched to prevent duplicate API calls
  const hasFetched = React.useRef(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    department: '',
    designation: '',
    nominationStatus: '',
    okrStatus: ''
  });
  const [tempFilters, setTempFilters] = useState({
    department: '',
    designation: '',
    nominationStatus: '',
    okrStatus: ''
  });

  // Fetch nominations from API
  const fetchNominations = useCallback(async () => {
    if (authLoading || !user?.email) {
      return;
    }
    
    // Prevent duplicate API calls
    if (hasFetched.current) {
      return;
    }
    hasFetched.current = true;

    setLoading(true);
    
    try {
      console.log('🔄 Fetching nomination approval list for user:', user.email);
      
      // Fetch real data from API
      const response = await nominationApprovalService.getNominationApprovalList(user.email);
      
      console.log('📋 Full API response:', response);
      console.log('📋 Response status:', response.status);
      console.log('📋 Response data:', response.data);
      console.log('📋 Response data type:', typeof response.data);
      console.log('📋 Response data structure:', Object.keys(response.data || {}));
      
      if (response.status && response.data?.data?.nomination_review_list) {
        console.log('📋 Raw nomination list from API:', response.data.data.nomination_review_list);
        
        // Transform API data to component format
        const transformedData = nominationApprovalService.transformApiData(
          response.data.data.nomination_review_list
        );
        
        console.log('✅ Transformed nomination data:', transformedData);
        console.log('📊 Number of nominations:', transformedData.length);
        setNominations(transformedData);
      } else if (response.data && 'nomination_review_list' in response.data) {
        // Handle case where data is directly in response.data
        console.log('📋 Found nomination_review_list directly in response.data');
        const dataWithList = response.data as { nomination_review_list: NominationApprovalItem[] };
        console.log('📋 Raw nomination list:', dataWithList.nomination_review_list);
        
        const transformedData = nominationApprovalService.transformApiData(
          dataWithList.nomination_review_list
        );
        
        console.log('✅ Transformed nomination data (direct):', transformedData);
        setNominations(transformedData);
      } else {
        console.error('❌ API returned error:', response.error || response.message);
        console.error('❌ Response structure:', response);
        setNominations([]);
      }
    } catch (error) {
      console.error('❌ Error fetching nominations:', error);
      
      // Check for 502 Bad Gateway error - navigate to full error page
      if (error && typeof error === 'object' && 'status_code' in error) {
        const statusCode = (error as { status_code: number }).status_code;
        if (statusCode === 502) {
          navigate('/error', {
            state: {
              errorType: 'bad-gateway',
              previousUrl: window.location.pathname,
              retryUrl: window.location.pathname
            }
          });
          return;
        }
      }
      
      setNominations([]);
    } finally {
      setLoading(false);
    }
  }, [user?.email, authLoading, navigate]);

  useEffect(() => {
    fetchNominations();
  }, [fetchNominations]);

  // Handler for removing all filters - defined early for useEffect dependency
  const handleRemoveAllFilters = useCallback(() => {
    const emptyFilters = {
      department: '',
      designation: '',
      nominationStatus: '',
      okrStatus: ''
    };
    setFilters(emptyFilters);
    setTempFilters(emptyFilters);
    setSearchTerm('');
    setPage(1);
  }, []);

  // Listen for global clearFilters event from SearchFilterBar
  useEffect(() => {
    const handler = () => {
      handleRemoveAllFilters();
    };

    window.addEventListener('clearFilters', handler as EventListener);
    return () => window.removeEventListener('clearFilters', handler as EventListener);
  }, [handleRemoveAllFilters]);

  // Filter nominations based on search term and filters
  // Best Practice: Memoize filtered results to avoid unnecessary recalculations
  const filteredNominations = React.useMemo(() => {
    return nominations.filter(nomination => {
      // Search filter - check multiple fields
      const matchesSearch = !searchTerm || (
        nomination.employeeName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        nomination.employeeId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        nomination.department.toLowerCase().includes(searchTerm.toLowerCase()) ||
        nomination.designation.toLowerCase().includes(searchTerm.toLowerCase()) ||
        nomination.managerName.toLowerCase().includes(searchTerm.toLowerCase())
      );

      // Department filter
      const matchesDepartment = !filters.department || nomination.department === filters.department;
      
      // Designation filter
      const matchesDesignation = !filters.designation || nomination.designation === filters.designation;
      
      // Nomination Status filter
      const matchesNominationStatus = !filters.nominationStatus || nomination.nominationStatus === filters.nominationStatus;
      
      // OKR Status filter - handle case-insensitive comparison and null/undefined values
      const matchesOkrStatus = !filters.okrStatus || (
        nomination.okrStatus && 
        nomination.okrStatus.toLowerCase() === filters.okrStatus.toLowerCase()
      );

      // Return true only if all active filters match
      return matchesSearch && matchesDepartment && matchesDesignation && matchesNominationStatus && matchesOkrStatus;
    });
  }, [nominations, searchTerm, filters]);

  // Debug logging
  console.log('🔍 Debug - nominations array:', nominations);
  console.log('🔍 Debug - nominations length:', nominations.length);
  console.log('🔍 Debug - filteredNominations length:', filteredNominations.length);
  console.log('🔍 Debug - searchTerm:', searchTerm);
  console.log('🔍 Debug - filters:', filters);

  const totalItems = filteredNominations.length;
  const pageCount = pageSize === 0 ? 1 : Math.ceil(totalItems / pageSize);
  const startIndex = (page - 1) * (pageSize === 0 ? totalItems : pageSize);
  const endIndex = pageSize === 0 ? totalItems : Math.min(startIndex + pageSize, totalItems);
  const currentNominations = pageSize === 0 ? filteredNominations : filteredNominations.slice(startIndex, endIndex);

  // Handlers
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleItemsPerPageChange = (value: number) => {
    setPageSize(value);
    setPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setPage(1);
  };

  const handleFilterChange = (filterType: string, value: string) => {
    setTempFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handleApplyFilters = () => {
    setFilters(tempFilters);
    setShowFilters(false);
    setPage(1);
  };

  const handleOpenFilters = () => {
    setTempFilters(filters);
    setShowFilters(true);
  };

  const handleCloseFilters = () => {
    setShowFilters(false);
    setTempFilters(filters);
  };

  // Navigation handler
  const handleViewDetails = (nominationId: string, okrId?: number, hasNominationId?: boolean) => {
    // Validate identifier before navigation
    if (!nominationId || nominationId.trim() === '') {
      console.warn('⚠️ Cannot navigate: Invalid nomination identifier');
      showError('Invalid Nomination', 'Unable to view details for this nomination. Missing identifier.');
      return;
    }
    
    // Build query parameters
    const params = new URLSearchParams();
    if (okrId) {
      params.append('okr_id', okrId.toString());
    } else {
      // If no okr_id, pass flag to indicate OKR data is not available
      params.append('no_okr', 'true');
    }
    
    // Pass flag to indicate if nomination_id is null (using okr_id as identifier)
    if (hasNominationId === false) {
      params.append('no_nomination', 'true');
    }
    
    // Navigate with query parameters
    const queryString = params.toString();
    const url = queryString 
      ? `/approvals/${encodeURIComponent(nominationId)}?${queryString}`
      : `/approvals/${encodeURIComponent(nominationId)}`;
    
    navigate(url);
  };

  // Get unique values for filters
  // Best Practice: Memoize unique values to avoid recalculating on every render
  const uniqueDepartments = React.useMemo(() => 
    [...new Set(nominations.map(n => n.department).filter(Boolean))].sort(),
    [nominations]
  );
  
  const uniqueDesignations = React.useMemo(() => 
    [...new Set(nominations.map(n => n.designation).filter(Boolean))].sort(),
    [nominations]
  );
  
  // Status options dynamically from API data
  const uniqueNominationStatuses = React.useMemo(() => 
    [...new Set(nominations.map(n => n.nominationStatus).filter(Boolean))].sort() as string[],
    [nominations]
  );
  
  const uniqueOkrStatuses = React.useMemo(() => 
    [...new Set(nominations.map(n => n.okrStatus).filter(Boolean))].sort() as string[],
    [nominations]
  );

  // Check if any filters are active
  const hasActiveFilters = filters.department || filters.designation || filters.nominationStatus || filters.okrStatus;
  
  // Count active filters for badge display
  const activeFiltersCount = [filters.department, filters.designation, filters.nominationStatus, filters.okrStatus].filter(Boolean).length;

  // Helper function to convert status to title case
  const toTitleCase = (str: string) => {
    if (str.toUpperCase() === 'N/A') return 'N/A';
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status.toLowerCase()) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getOkrStatusBadgeClass = (status?: string) => {
    if (!status) return 'bg-gray-100 text-gray-800';
    
    const normalizedStatus = status.toLowerCase();
    switch (normalizedStatus) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'draft':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (!hasActiveCycle) {
    return <NoActiveCycle />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        <div className="text-center">
          <LoadingSpinner />
          <p className="mt-2 text-sm text-gray-600">Loading nominations...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <ErrorState
        type="not-found"
        title="Authentication Required"
        message="No user data available. Please log in to continue."
        showRetryButton={false}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Search and Action Section */}
      <div className="flex items-center gap-4">
        <div className="flex-grow">
          <SearchFilterBar
            searchTerm={searchTerm}
            onSearchChange={handleSearchChange}
            onClearSearch={() => handleSearchChange('')}
            onOpenFilters={handleOpenFilters}
            hasActiveFilters={!!hasActiveFilters}
            activeFiltersCount={activeFiltersCount}
            tuneIcon={tuneIcon}
          />
        </div>
      </div>

      <FiltersModal
        show={showFilters}
        tempFilters={{
          department: tempFilters.department,
          designation: tempFilters.designation,
          nominationCapacity: tempFilters.nominationStatus,
          okrStatus: tempFilters.okrStatus
        }}
        uniqueDepartments={uniqueDepartments}
        uniqueDesignations={uniqueDesignations}
        uniqueNominationCapacities={uniqueNominationStatuses}
        uniqueOkrStatuses={uniqueOkrStatuses}
        onChange={(filterType: string, value: string) => {
          // Map nominationCapacity back to nominationStatus
          const mappedFilterType = filterType === 'nominationCapacity' ? 'nominationStatus' : filterType;
          handleFilterChange(mappedFilterType, value);
        }}
        onApply={handleApplyFilters}
        onRemoveAll={handleRemoveAllFilters}
        onClose={handleCloseFilters}
        arrowDropDownIcon={arrowDropDownIcon}
      />
       
     

      {/* Table Card */}
      <Card>
        <div className="table-container">
          {/* Table Header */}
          <div className="bg-primary-800 rounded-lg px-6 py-3 border-b border-gray-200 mb-2">
            <div className="grid gap-4 text-left text-base font-medium text-white tracking-wider" 
                 style={{ gridTemplateColumns: 'minmax(80px, 110px) minmax(140px, 1fr) minmax(120px, 120px) minmax(100px, 140px) minmax(140px, 1fr) minmax(120px, 160px) minmax(100px, 130px)' }}>
              <div className="text-base">Employee ID</div>
              <div className="text-base">Employee Name</div>
              <div className="text-base">Designation</div>
              <div className="text-base">Department</div>
              <div className="text-base">Manager Name</div>
              <div className="text-base">GW 360 Feedback status</div>
              <div className="text-base">OKR Status</div>
            </div>
          </div>

          {/* Table Body */}
          <div className="space-y-3 relative">
            {currentNominations.length === 0 ? (
              <div className="text-center py-12">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100 mb-4">
                  <svg className="h-6 w-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Nominations Found</h3>
                <p className="text-gray-500 mb-4">
                  {hasActiveFilters 
                    ? 'No nominations match your current search criteria.'
                    : 'No nominations available for approval.'
                  }
                </p>
              </div>
            ) : (
              currentNominations.map((nomination) => (
                <div 
                  key={nomination.id || `${nomination.employeeId}-${nomination.employeeEmail}`} 
                  className={`border border-gray-200 rounded-lg px-6 py-2 transition-colors relative ${
                    nomination.id && nomination.id.trim() !== '' 
                      ? 'hover:bg-gray-50 cursor-pointer' 
                      : 'opacity-60 cursor-not-allowed'
                  }`}
                  onClick={() => {
                    if (nomination.id && nomination.id.trim() !== '') {
                      handleViewDetails(
                        nomination.id, 
                        nomination.okrId, 
                        nomination.nominationId !== null && nomination.nominationId !== undefined
                      );
                    }
                  }}
                >
                  <div className="grid gap-4 items-center" 
                       style={{ gridTemplateColumns: 'minmax(80px, 110px) minmax(140px, 1fr) minmax(120px, 120px) minmax(100px, 140px) minmax(140px, 1fr) minmax(120px, 160px) minmax(100px, 130px)' }}>
                    <div className="flex">
                      <div className="flex items-center gap-2">
                        <div className="text-base text-gray-600 break-words">{nomination.employeeId}</div>
                        {(!nomination.id || nomination.id.trim() === '') && (
                          <span className="text-xs text-red-500" title="Cannot view details - Missing identifier">
                            ⚠️
                          </span>
                        )}
                      </div>
                    </div>
                    <div>
                      <NameWithEmailTooltip
                        as="div"
                        className="text-base text-gray-600"
                        name={nomination.employeeName}
                        email={nomination.employeeEmail}
                      />
                    </div>
                    <div>
                      <div className="text-base text-gray-600">{nomination.designation}</div>
                    </div>
                    <div>
                      <div className="text-base text-gray-600">{nomination.department}</div>
                    </div>
                    <div>
                      <NameWithEmailTooltip
                        as="div"
                        className="text-base text-gray-600"
                        name={nomination.managerName}
                        email={nomination.managerEmail}
                      />
                    </div>
                    <div>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(nomination.nominationStatus)}`}>
                        {toTitleCase(nomination.nominationStatus) || 'N/A'}
                      </span>
                    </div>
                    <div>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getOkrStatusBadgeClass(nomination.okrStatus?.toLowerCase())}`}>
                        {nomination.okrStatus===
                        "N/A"?"N/A":  toTitleCase(nomination.okrStatus!) || 'N/A'}
                        </span>
                    </div>
                    {/* <div className="col-span-1">
                      <div className="flex items-center justify-center">
                        <div className="relative">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleMenu(nomination.id, e);
                            }}
                            className="p-2 text-black hover:text-gray-600 focus:outline-none transition-colors rounded-full hover:bg-gray-100"
                          >
                            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                            </svg>
                          </button>
                          
                         
                          {openMenuId === nomination.id && (
                            <div 
                              className={`absolute right-0 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-[9999] ${
                                menuPosition === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'
                              }`}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="py-1">
                                <button 
                                  onClick={() => handleViewDetails(
                                    nomination.id, 
                                    nomination.okrId,
                                    nomination.nominationId !== null && nomination.nominationId !== undefined
                                  )}
                                  className="flex items-center w-full px-4 py-2 text-base text-black hover:bg-gray-100"
                                >
                                  View Details
                                </button>
                           
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div> */}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Pagination - Only show when there are results */}
        {pageSize > 0 && totalItems > 0 && (
          <div className="px-4 py-3 border-t border-gray-200">
            <Pagination
              page={page}
              pageCount={pageCount}
              onPageChange={handlePageChange}
              pageSize={pageSize}
              onPageSizeChange={handleItemsPerPageChange}
              pageSizeOptions={PageSizeOptions}
            />
          </div>
        )}
      </Card>

      {/* Toast Dialog */}
      <Dialog
        isOpen={toastState.isOpen}
        onClose={closeToast}
        title={toastState.title}
        message={toastState.message}
        type={toastState.type}
        onConfirm={toastState.onConfirm || closeToast}
        showCancel={toastState.showCancel}
        confirmText={toastState.confirmText}
        cancelText={toastState.cancelText}
      />
    </div>
  );
};

export default NominationApprovalPageView;
